import { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { setGroupsData } from 'api/client-side-slices/GroupsSlice';
import { useGetAllGroupsQuery, usePrimeSearchMutation } from 'api/EntitiesApiSlice';
import { useAppDispatch } from 'app-redux/hooks';
import {
  CheckboxFilter,
  DateRangeFilter,
  filterByDateRange,
  FilterChip,
  filterByValues,
  getFilterOptions,
  sortTableData,
  TableComponent,
} from 'components';
import { format } from 'date-fns';
import { useDebounce } from 'use-debounce';
import { Status } from 'utils';
import {
  emptyGroupFilters,
  getGroupColumns,
  GroupFilters,
  groupFilterValues,
  GroupSort,
  groupSortValues,
} from 'utils/constants';
import { TableHeader } from './components/TableHeader';

export type GroupTableData = {
  registeredName: ReactNode;
  entityTypeName: ReactNode;
  abn: ReactNode;
  status?: Status;
  assignedTo: ReactNode;
  lastUpdated: ReactNode;
  location: string;
  acn: string;
  id: number;
};

export const GroupsTab = () => {
  const [searchValue, setSearchValue] = useState('');
  const [activeFilter, setActiveFilter] = useState<string>('');
  const [hasSearched, setHasSearched] = useState(false);
  const [sort, setSort] = useState<GroupSort>({
    columnKey: 'statusUpdatedAt',
    direction: 'descending',
  });
  const [columnFilters, setColumnFilters] = useState<GroupFilters>(emptyGroupFilters);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { data: groupsData } = useGetAllGroupsQuery();
  const [fetchFilters, { data: filtersData, isLoading }] = usePrimeSearchMutation();
  const [debouncedSearchValue] = useDebounce(searchValue, 500);

  const fetchFilteredData = (searchTerm: string, status: string) => {
    setHasSearched(searchTerm !== '' || status !== '');
    fetchFilters({
      searchTerm,
      status,
      pageSize: 99999999,
      pageNumber: 1,
    });
  };

  const handleFilterClick = (filter: string) => {
    setActiveFilter(filter);
    fetchFilteredData(debouncedSearchValue, filter);
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  const handleRowClick = (record: GroupTableData) => {
    dispatch(setGroupsData(record));
    navigate(`/entity-details/${record.id}`);
  };

  // Options come from the records the search returned, so the panels only ever offer
  // values that are actually present.
  const filterOptions = {
    entityTypeName: getFilterOptions(filtersData, groupFilterValues.entityTypeName),
    assignedToUser: getFilterOptions(filtersData, groupFilterValues.assignedToUser),
  };

  const filteredData = filterByValues(
    filterByValues(
      filterByDateRange(
        filtersData,
        (record) => record.statusUpdatedAt,
        columnFilters.statusUpdatedAt
      ),
      groupFilterValues.entityTypeName,
      columnFilters.entityTypeName
    ),
    groupFilterValues.assignedToUser,
    columnFilters.assignedToUser
  );

  const sortedGroupsData = sortTableData(filteredData, sort, groupSortValues);

  const columns = getGroupColumns({
    sort,
    onSortChange: setSort,
    filters: columnFilters,
    onFiltersChange: setColumnFilters,
    filterOptions,
  });

  const updateFilters = (changes: Partial<GroupFilters>) =>
    setColumnFilters({ ...columnFilters, ...changes });

  const lastUpdatedRange = columnFilters.statusUpdatedAt;

  const describeSelection = (selected: string[]) =>
    selected.length === 1 ? selected[0] : `${selected.length} selected`;

  const getNoDataMessage = () => {
    if (!hasSearched) {
      return 'You have no KYC verifications started. Start your first KYC verification today!';
    }
    return 'No records found';
  };

  useEffect(() => {
    if (searchValue === '') {
      fetchFilteredData('', activeFilter);
    }
  }, [searchValue]);

  useEffect(() => {
    if (debouncedSearchValue !== '') {
      fetchFilteredData(debouncedSearchValue, activeFilter);
    }
  }, [debouncedSearchValue, activeFilter]);

  return (
    <div className="mb-[80px]">
      <TableHeader
        activeFilter={activeFilter}
        data={groupsData}
        handleFilterClick={handleFilterClick}
        onChange={handleSearch}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
      />

      <div className="table-filter-chips">
        {columnFilters.entityTypeName.length > 0 && (
          <FilterChip
            label={`Type: ${describeSelection(columnFilters.entityTypeName)}`}
            panel={
              <CheckboxFilter
                options={filterOptions.entityTypeName}
                value={columnFilters.entityTypeName}
                onChange={(entityTypeName) => updateFilters({ entityTypeName })}
              />
            }
            onClear={() => updateFilters({ entityTypeName: [] })}
          />
        )}

        {columnFilters.assignedToUser.length > 0 && (
          <FilterChip
            label={`Assigned To: ${describeSelection(columnFilters.assignedToUser)}`}
            panel={
              <CheckboxFilter
                options={filterOptions.assignedToUser}
                value={columnFilters.assignedToUser}
                onChange={(assignedToUser) => updateFilters({ assignedToUser })}
              />
            }
            onClear={() => updateFilters({ assignedToUser: [] })}
          />
        )}

        {lastUpdatedRange && (
          <FilterChip
            label={`Last Updated: ${format(lastUpdatedRange[0], 'dd/MM/yyyy')} - ${format(
              lastUpdatedRange[1],
              'dd/MM/yyyy'
            )}`}
            panel={
              <DateRangeFilter
                value={lastUpdatedRange}
                onChange={(statusUpdatedAt) => updateFilters({ statusUpdatedAt })}
              />
            }
            onClear={() => updateFilters({ statusUpdatedAt: null })}
          />
        )}
      </div>

      <TableComponent
        columns={columns}
        noDataMessage={getNoDataMessage()}
        dataSource={sortedGroupsData}
        className="entities-table"
        isLoading={isLoading}
        onRow={(record: GroupTableData) => ({
          onClick: () => handleRowClick(record),
        })}
      />
    </div>
  );
};
